import { useEffect, useState } from 'react';
import { deleteDaily, emptyDaily, getDaily, saveDaily } from '../../db/dailies';
import { useAsync } from '../hooks/useAsync';
import { useDataVersion } from '../../db/events';
import { isDailyEmpty, type DailySummary } from '../../domain/types';
import type { DailyRecord } from '../../db/schema';

const FIELDS = [
  { key: 'events', label: '今日の主な出来事', placeholder: '何があったか' },
  { key: 'good', label: 'よかったこと', placeholder: 'うまくいったこと・ありがたかったこと' },
  { key: 'action', label: '明日試すワンアクション', placeholder: '明日ひとつだけ試すこと' },
] as const;

/** その日の「一日のまとめ」。日ビューの先頭に置く。 */
export function DailyCard({ date }: { date: string }) {
  const version = useDataVersion();
  const saved = useAsync<DailyRecord | undefined>(() => getDaily(date), [date, version], undefined);
  const [draft, setDraft] = useState<DailySummary | null>(null);
  const [editing, setEditing] = useState(false);

  useEffect(() => {
    setDraft(null);
    setEditing(false);
  }, [date]);

  const current = draft ?? saved ?? emptyDaily(date);
  const empty = isDailyEmpty(current);

  const commit = async (next: DailySummary) => {
    setDraft(next);
    await saveDaily(next);
  };

  if (!editing && empty) {
    return (
      <button className="daily-empty" onClick={() => setEditing(true)}>
        ＋ 一日のまとめを書く
      </button>
    );
  }

  if (!editing) {
    return (
      <div className="daily-card">
        <div className="daily-head">
          <h2>一日のまとめ</h2>
          <button className="icon-btn" onClick={() => setEditing(true)} aria-label="編集">
            ✎
          </button>
        </div>
        {FIELDS.map((f) =>
          current[f.key].trim() ? (
            <div key={f.key} className="daily-item">
              <span className="daily-label">{f.label}</span>
              <p className="daily-text">{current[f.key]}</p>
            </div>
          ) : null,
        )}
      </div>
    );
  }

  return (
    <div className="daily-card is-editing">
      <div className="daily-head">
        <h2>一日のまとめ</h2>
        <button
          className="today-btn"
          onClick={async () => {
            if (draft) await saveDaily(draft);
            setDraft(null);
            setEditing(false);
          }}
        >
          完了
        </button>
      </div>
      {FIELDS.map((f) => (
        <label key={f.key} className="field">
          <span className="field-label">{f.label}</span>
          <textarea
            rows={2}
            value={current[f.key]}
            placeholder={f.placeholder}
            onChange={(e) => setDraft({ ...current, [f.key]: e.target.value })}
            onBlur={() => draft && void commit(draft)}
          />
        </label>
      ))}
      {!empty && (
        <div className="btn-row">
          <button
            className="danger-btn"
            onClick={async () => {
              if (!confirm('この日のまとめを削除します。よろしいですか？')) return;
              await deleteDaily(date);
              setDraft(null);
              setEditing(false);
            }}
          >
            削除
          </button>
        </div>
      )}
    </div>
  );
}
